import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import App from 'app/components/App.tsx';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { LoadingStatus } from 'app/store/types.ts';
import { FallbackPage } from 'app/components/FallbackPage/index.tsx';
import NavigationDrawer from 'app/components/NavigationDrawer/index.ts';
import DefaultLoadingPage from 'app/components/DefaultLoadingPage.tsx';
import { useIsAuthenticated } from '@azure/msal-react';
import appInsights from './components/AppInsights.tsx';
import { Stack } from '@mui/system';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { createTranslationField } from 'app/utils/i18nUtils.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import CommandPalette from 'app/components/CommandPalette';

const AppContainerContent: React.FC<{ isHeiDomain?: boolean }> = ({ isHeiDomain = false }) => {
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const currentUserNotFound = useSelector(UserSelectors.CurrentUserNotFoundSelector);
  const currentUserLoadingStatus = useSelector(UserSelectors.CurrentUserLoadingStatusSelector);
  const currentUserLoadError = useSelector(UserSelectors.CurrentUserLoadErrorSelector);
  const isAuthenticated = useIsAuthenticated();
  const [translate] = useTranslation([I18nNamespace.Common]);
  const i18nCommon = createTranslationField<I18nCommonNs>();
  const lastTrackedFallbackKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      appInsights.setAuthenticatedUserContext(currentUser.id.toString(), undefined, true);
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [currentUser, isAuthenticated]);

  useEffect(() => {
    if (currentUserLoadingStatus !== LoadingStatus.Failed || !currentUserLoadError) {
      return;
    }

    const diagnosticId = currentUserLoadError.requestId;
    const diagnosticTimestamp = currentUserLoadError.timestamp;
    const failureType = currentUserLoadError.status === 404 ? 'user_not_found' : 'user_load_failed';
    const fallbackKey = `${failureType}:${diagnosticId ?? 'missing'}:${diagnosticTimestamp}`;

    if (lastTrackedFallbackKeyRef.current === fallbackKey) {
      return;
    }

    lastTrackedFallbackKeyRef.current = fallbackKey;

    const properties = {
      failureType,
      diagnosticId,
      diagnosticTimestamp,
      status: currentUserLoadError.status,
      errorCode: currentUserLoadError.errorCode,
      isNetworkError: currentUserLoadError.isNetworkError,
      isAuthenticated,
      hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
      path: typeof window === 'undefined' ? undefined : window.location.pathname
    };

    appInsights.trackEvent({
      name: 'CurrentUserLoadFallbackShown',
      properties
    });

    appInsights.trackTrace(
      {
        message: `CurrentUserLoadFallbackShown diagnosticId=${diagnosticId ?? 'missing'}`
      },
      properties
    );
  }, [currentUserLoadingStatus, currentUserLoadError, isAuthenticated]);

  if (currentUserNotFound && currentUserLoadingStatus === LoadingStatus.Failed) {
    return (
      <FallbackPage
        redirectUrl={null}
        title={
          isHeiDomain
            ? translate(i18nCommon.fallbackPage.unauthorizedLoadErrorTitle)
            : 'The user does not belong to an organization'
        }
        text={
          isHeiDomain
            ? translate(i18nCommon.fallbackPage.unauthorizedLoadErrorMessage)
            : 'Make sure you registered using the invitation link sent to your email. If you have not yet received this, please contact our support at support@sklls.ai'
        }
        diagnosticId={currentUserLoadError?.requestId}
        diagnosticTimestamp={currentUserLoadError?.timestamp}
        showLogoutButton={true}
      />
    );
  }

  if (currentUserLoadingStatus === LoadingStatus.Failed) {
    return (
      <FallbackPage
        redirectUrl={null}
        title={
          isHeiDomain
            ? translate(i18nCommon.fallbackPage.unauthorizedLoadErrorTitle)
            : 'Unable to Load User Information'
        }
        text={
          isHeiDomain
            ? translate(i18nCommon.fallbackPage.unauthorizedLoadErrorMessage)
            : "We're having trouble loading your account information. Please try refreshing the page or logging out and back in."
        }
        diagnosticId={currentUserLoadError?.requestId}
        diagnosticTimestamp={currentUserLoadError?.timestamp}
        showLogoutButton={true}
      />
    );
  }

  if (!(currentUser && isAuthenticated)) {
    return <DefaultLoadingPage />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      {!isHeiDomain && <NavigationDrawer />}
      <CommandPalette />
      <App />
    </Stack>
  );
};

export default AppContainerContent;
